import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { companyInfo, serviceAreas } from '../data/mockData';
import { toast } from 'sonner';
import axios from 'axios';
import { AddressAutocomplete } from './AddressAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    service: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Submit to backend API
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        toast.success(response.data.message);
        setFormData({ name: '', phone: '', email: '', address: '', service: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try calling us instead.');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-block bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Get In Touch
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Request Your Free Estimate
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ready to get started? Contact us today for a free, no-obligation estimate on your tree care or landscaping project.
          </p>
        </div>

        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Let's Discuss Your Project
              </h3>
              <p className="text-lg text-gray-600 mb-8">
                Whether you need emergency tree removal, routine maintenance, or complete landscaping services, our team is ready to help.
              </p>
            </div>

            {/* Contact Cards */}
            <div className="space-y-4">
              <Card className="border-2 border-green-500/20 hover:border-green-500 transition-all duration-300">
                <CardContent className="p-6 flex items-start space-x-4">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <Phone className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Phone</h4>
                    <a
                      href={`tel:${companyInfo.phone.replace(/\D/g, '')}`}
                      className="text-green-600 text-lg font-semibold hover:text-green-700 block"
                    >
                      {companyInfo.phone}
                    </a>
                    <a
                      href={`tel:${companyInfo.phoneSecondary.replace(/\D/g, '')}`}
                      className="text-green-600 text-lg font-semibold hover:text-green-700 block"
                    >
                      {companyInfo.phoneSecondary}
                    </a>
                    <p className="text-sm text-gray-600 mt-1">Available 24/7 for emergencies</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-500/20 hover:border-green-500 transition-all duration-300">
                <CardContent className="p-6 flex items-start space-x-4">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <Clock className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Hours</h4>
                    <p className="text-gray-700">{companyInfo.hours}</p>
                    <p className="text-sm text-gray-600 mt-1">24/7 Emergency Response Available</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-500/20 hover:border-green-500 transition-all duration-300">
                <CardContent className="p-6 flex items-start space-x-4">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <MapPin className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Service Area</h4>
                    <p className="text-gray-700">{companyInfo.location}</p>
                    <p className="text-sm text-gray-600 mt-1">& surrounding areas</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Service Areas */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <h4 className="font-bold text-gray-900 mb-4 text-xl">We Serve:</h4>
              <div className="grid grid-cols-2 gap-3">
                {serviceAreas.map(area => (
                  <div key={area} className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-gray-700">{area}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="border-2 border-green-500/20 shadow-2xl">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="John Doe"
                    className="border-2 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <Input
                    required
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="(615) 123-4567"
                    className="border-2 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="john@example.com"
                    className="border-2 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Service Address *
                  </label>
                  <AddressAutocomplete
                    value={formData.address}
                    onChange={(value) => handleChange('address', value)}
                    required
                    placeholder="Enter your service address"
                  />
                  <p className="text-xs text-gray-500 mt-1">Start typing and select from the dropdown</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Service Needed *
                  </label>
                  <Input
                    required
                    value={formData.service}
                    onChange={(e) => handleChange('service', e.target.value)}
                    placeholder="e.g., Tree Removal, Landscaping"
                    className="border-2 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Project Details *
                  </label>
                  <Textarea
                    required
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    placeholder="Tell us about your project..."
                    rows={5}
                    className="border-2 focus:border-green-500"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-bold text-lg py-6"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Send Message
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
